import {
    AccessCodeChange,
    BarcodeReader,
    Box, Device, EndOccupancyOptions, Keypad,
    Lock,
    LockStatus,
    LogLevel,
    NfcReader,
    Occupancy,
    OccupancyContent,
    OccupyRequest,
    PaymentTerminal
} from "@variocube/app-api";
import {VcmpClient} from "@variocube/vcmp";
import {AbstractController} from "./AbstractController";
import {SupportInfo, Controller, InternetConnection, Permissions} from "./Controller";
import {ControllerNfcReader} from "./ControllerNfcReader";
import {ControllerPaymentTerminal} from "./ControllerPaymentTerminal";
import {ControllerBarcodeReader} from "./ControllerBarcodeReader";
import {createQueryString, defined} from "@variocube/app-common";
import {logger} from "../logger";
import {ControllerKeypad} from "./ControllerKeypad";
import {wsPrefix} from "../urlParams";
import { ControllerDoorbell } from "./ControllerDoorbell";

const log = logger.sub("CubeController");

function createJsonRequestInit(method: "put" | "post" | "delete", value: any): RequestInit {
    return {
        method: method,
        ...(defined(value) && {
            body: JSON.stringify(value),
            headers: {
                "Content-type": "application/json"
            }
        })
    };
}

enum LockEventType {
    added = "added",
    removed = "removed",
    status = "status"
}

interface LockEvent {
    type: LockEventType;
    lock: Lock;
}

interface LocationCode {
    locationCode: string;
}

interface SupportInfoEvent {
    supportInfo: SupportInfo;
}

interface InstalledAppsChanged {
    apps: string[];
}

interface StorageItemChanged {
    appId: string;
    key: string;
}

interface InternetConnectionChanged {
    connected: boolean;
}

interface BuildProperties {
    group: string;
    artifact: string;
    name: string;
    version: string;
    time: string;
}

interface System {
    hostname: string;
    stage: string;
    region: string;
    build: BuildProperties;
    setupUrl: string|null;
}

export class CubeController extends AbstractController implements Controller {

    private readonly baseUrl: string;

    private system?: System;

    public readonly nfcReader: NfcReader;
    public readonly paymentTerminal: PaymentTerminal;
    public readonly barcodeReader: BarcodeReader;
    public readonly keypad: Keypad;
    public readonly doorbell: ControllerDoorbell;

    constructor(private readonly host: string = location.hostname, public readonly port: number = 9000) {
        super();

        this.baseUrl = "http://" + this.host + ":" + this.port;

        this.nfcReader = new ControllerNfcReader(`${wsPrefix()}://${this.host}:${this.port}/nfc-reader`);
        this.paymentTerminal = new ControllerPaymentTerminal(`${wsPrefix()}://${this.host}:${this.port}/payment-terminal`);
        this.barcodeReader = new ControllerBarcodeReader(`${wsPrefix()}://${this.host}:${this.port}/barcode-reader`);
        this.keypad = new ControllerKeypad(`${wsPrefix()}://${this.host}:${this.port}/keypad`);
        this.doorbell = new ControllerDoorbell(`${wsPrefix()}://${this.host}:${this.port}/doorbell`);

        const client = new VcmpClient(`${wsPrefix()}://${this.host}:${this.port}/events`);
        client.on("controller:InstalledAppsChanged", (event: InstalledAppsChanged) => {
            log.info("Received installed apps changed", event);
            this.onInstalledAppsChanged(event.apps);
        });
        client.on("controller:StorageItemChanged", (event: StorageItemChanged) => {
            log.info("Received storage item changed", event);
            this.onStorageItemChanged(event.appId, event.key);
        });
        client.on("controller:InternetConnectionChanged", (event: InternetConnectionChanged) => {
            log.info("Received internet connection changed", event);
            this.onInternetConnectionChanged(event);
        });
        client.on("controller:LocationCode", (event: LocationCode) => {
            log.info("Received location code", event);
            this.setLocationCode(event.locationCode);
        });
        client.on("controller:SupportInfo", ({supportInfo}: SupportInfoEvent) => {
            log.info("Received support info", supportInfo);
            this.supportInfo.safeInvokeListeners(supportInfo);
        });
        client.on("controller:LockEvent", (event: LockEvent) => {
            log.info("Received lock event", event);
            if (event.type == LockEventType.status || event.type == LockEventType.added) {
                this.onLockStatusChanged(event.lock.handle, event.lock.status);
            }
        });
        client.on("controller:OccupancyCreated", ({occupancy}: { occupancy: Occupancy }) => {
            log.info("Received occupancy created", occupancy);
            this.onOccupancyCreated(occupancy);
        });
        client.on("controller:OccupancyUpdated", ({occupancy}: { occupancy: Occupancy }) => {
            log.info("Received occupancy updated", occupancy);
            this.onOccupancyUpdated(occupancy);
        });
        client.on("controller:OccupancyAccessChanged", ({occupancy}: { occupancy: Occupancy }) => {
            log.info("Received occupancy access changed", occupancy);
            this.onOccupancyAccessChanged(occupancy);
        });
        client.on("controller:OccupancyEnded", ({uuid}: { uuid: string }) => {
            log.info("Received occupancy ended", uuid);
            this.onOccupancyEnded(uuid);
        });
        client.on("controller:BoxesChanged", ({boxes}: { boxes: Box[] }) => {
            log.info("Received boxes changed", boxes);
            this.onBoxesChanged(boxes);
        });
        client.on("controller:DeviceListChanged", ({devices}: {devices: Device[]}) => {
            log.info("Received device list changed", devices);
            this.onDevicesChanged(devices);
        });
        client.on("controller:SetupUrl", ({setupUrl}: { setupUrl: string|null }) => {
            log.info("Received new setup url", setupUrl);
            this.setSetupUrl(setupUrl);
        });
        client.onOpen = async () => {
            log.info("Controller connection open");
            this.system = await this.getSystem();

            this.setSetupUrl(this.system.setupUrl);

            try {
                const locationCode = await this.getLocationCode();
                this.setLocationCode(locationCode.locationCode);
            } catch (error) {
                log.error("Could not fetch location code.", error);
            }

            this.onInstalledAppsChanged(await this.getApps());
            this.onConnected(true);
            this.onInternetConnectionChanged(await this.getInternetConnection());
            this.supportInfo.safeInvokeListeners(await this.getSupportInfo());
        };
        client.onClose = () => {
            log.info("Controller connection closed");
            this.onConnected(false);
        };
        client.start();
    }

    get version(): string | undefined {
        return this.system && this.system.build.version;
    }

    get hostname() {
        return (this.system && this.system.hostname) || "localhost";
    }

    get stage() {
        return (this.system && this.system.stage) || "local";
    }

    get region() {
        return (this.system && this.system.region) || "local";
    }

    async getInternetConnection() {
        const response = await window.fetch(`${this.baseUrl}/internet-connection`);
        if (!response.ok) {
            throw new Error("Could query internet connection");
        }
        return await response.json() as InternetConnection;
    }

    private async getLocationCode() {
        const response = await window.fetch(`${this.baseUrl}/location-code`);
        if (!response.ok) {
            throw new Error("Could query location code.");
        }
        return await response.json() as LocationCode;
    }

    async getStorageItemInternal(appId: string, key: string) {
        const response = await window.fetch(`${this.baseUrl}/apps/${appId}/${key}`);
        if (response.status == 404) {
            return undefined;
        }
        if (!response.ok) {
            throw new Error("Key could not be retrieved");
        }
        return response;
    }

    async getStorageItem<T = any>(appId: string, key: string) {
        const response = await this.getStorageItemInternal(appId, key);
        return response && await response.json() as T;
    }

    async getStorageItemBlob(appId: string, key: string) {
        const response = await this.getStorageItemInternal(appId, key);
        return response && await response.blob();
    }

    private async getLock(lockHandle: string) {
        const response = await window.fetch(`${this.baseUrl}/locks/${lockHandle}`);
        if (!response.ok) {
            throw new Error("Lock could not be retrieved");
        }
        return await response.json() as Lock;
    }

    async getStatus(lockHandle: string) {
        const lock = await this.getLock(lockHandle);
        return lock.status;
    }

    async open(lockHandle: string) {
        const response = await window.fetch(`${this.baseUrl}/locks/${lockHandle}`, createJsonRequestInit("put", {status: LockStatus.Open}));
        if (!response.ok) {
            throw new Error("Open lock failed");
        }
    }

    async confirmOccupancy(occupancyId: string, content?: OccupancyContent, merge?: boolean) {
        log.info("Confirming occupancy", occupancyId);
        const response = await window.fetch(`${this.baseUrl}/occupancies/${occupancyId}/confirm?merge=${Boolean(merge)}`, createJsonRequestInit("post", content));
        if (!response.ok) {
            throw new Error("Failed to confirm occupancy.");
        }
    }

    async cancelOccupancy(occupancyId: string) {
        log.info("Canceling occupancy", occupancyId);
        const response = await window.fetch(`${this.baseUrl}/occupancies/${occupancyId}/cancel`, {method: "post"});
        if (!response.ok) {
            throw new Error("Failed to cancel occupancy.");
        }
    }

    async endOccupancy(occupancyId: string, options?: EndOccupancyOptions) {
        log.info("Ending occupancy", occupancyId, options);
        const response = await window.fetch(`${this.baseUrl}/occupancies/${occupancyId}`, createJsonRequestInit("delete", options));
        if (!response.ok) {
            throw new Error("Failed to end occupancy.");
        }
    }

    async occupy(request: OccupyRequest) {
        log.info("Occupy", request);
        const url = `${this.baseUrl}/occupancies`;
        const response = await window.fetch(url, createJsonRequestInit("post", request));
        if (!response.ok) {
            console.error(`Cannot window.fetch ${url}: ${response.status}, ${response.statusText}`);
            console.error(request);
            console.error(response);
            throw new Error("Occupy failed");
        }
        return await response.json() as Occupancy;
    }

    async updateOccupancy(occupancyId: string, content: OccupancyContent, merge: boolean) {
        log.info("Updating occupancy", occupancyId, {content, merge});
        const url = `${this.baseUrl}/occupancies/${occupancyId}?merge=${merge}`;
        const response = await window.fetch(url, createJsonRequestInit("put", content));
        if (!response.ok) {
            console.error(`Cannot window.fetch ${url}: ${response.status}, ${response.statusText}`);
            console.error(content);
            console.error(response);
            throw new Error("Failed to update occupancy.");
        }
    }

    async changeAccessCode(occupancyId: string, accessCode: string | AccessCodeChange) {
        log.info("Changing occupancy access code", occupancyId, accessCode);
        const url = `${this.baseUrl}/occupancies/${occupancyId}/accessCode`;

        const request = typeof accessCode != "string"
            ? createJsonRequestInit("put", accessCode)
            : {
                method: "put",
                body: accessCode,
                headers: {
                    "content-type": "text/plain"
                }
            };

        const response = await window.fetch(url, request);
        if (!response.ok) {
            log.error(`Cannot window.fetch ${url}: ${response.status}, ${response.statusText}`);
            log.error(response);
            throw new Error("Failed to change occupancy access code.");
        }
    }

    async changeAccessKeys(occupancyId: string, accessKeys: string[]) {
        log.info("Changing occupancy access keys", occupancyId, accessKeys);
        const url = `${this.baseUrl}/occupancies/${occupancyId}/accessKeys`;

        const response = await window.fetch(url, createJsonRequestInit("put", accessKeys));
        if (!response.ok) {
            console.error(`Cannot window.fetch ${url}: ${response.status}, ${response.statusText}`);
            console.error(response);
            throw new Error("Failed to change occupancy access keys.");
        }
    }

    async getOccupancies(appId?: string, access?: string): Promise<Occupancy[]> {
        const response = await window.fetch(`${this.baseUrl}/occupancies?${createQueryString({appId, access})}`);
        if (!response.ok) {
            throw new Error("Fetching occupancies failed");
        }
        return await response.json() as Occupancy[];
    }

    async getOccupancy(occupancyId: string) {
        const response = await window.fetch(`${this.baseUrl}/occupancies/${occupancyId}`);
        if (response.status == 404) {
            return undefined;
        }
        if (!response.ok) {
            throw new Error("Failed to retrieve occupancy.");
        }
        return await response.json() as Occupancy;
    }

    async getApps() {
        const response = await window.fetch(`${this.baseUrl}/apps`);
        if (!response.ok) {
            throw new Error("Fetching apps failed");
        }
        return await response.json() as string[];
    }

    private async getSystem() {
        const response = await window.fetch(`${this.baseUrl}/system`);
        if (!response.ok) {
            throw new Error("Fetching system information failed");
        }
        return await response.json() as System;
    }

    async verifyAuthCode(code: string): Promise<Permissions> {
        const response = await window.fetch(`${this.baseUrl}/permissions/${code}`);
        if (!response.ok) {
            throw new Error("invalid auth code");
        }
        return await response.json() as Permissions;
    }

    async getSupportInfo(): Promise<SupportInfo> {
        const response = await window.fetch(`${this.baseUrl}/support-info`);
        if (!response.ok) {
            throw new Error("invalid support info");
        }
        return await response.json() as SupportInfo;
    }


    async getLocks() {
        const response = await window.fetch(`${this.baseUrl}/locks`);
        if (!response.ok) {
            throw new Error("Fetching locks failed");
        }
        return await response.json() as Lock[];
    }

    async getBoxes() {
        const response = await window.fetch(`${this.baseUrl}/boxes`);
        if (!response.ok) {
            throw new Error("Fetching boxes failed");
        }
        return await response.json() as Box[];
    }

    async updateBoxMaintenanceState(boxNumber: string, requiredMaintenance: boolean) {
        const response = await window.fetch(
            `${this.baseUrl}/boxes/${boxNumber}/maintenance`,
            createJsonRequestInit("put", { requiredMaintenance })
        );
        if (!response.ok) {
            throw new Error("Update boxes maintenance state failed");
        }
    }

    /**
     * Restarts the controller.
     */
    async restartController() {
        log.info("Restarting controller");
        const response = await window.fetch(`${this.baseUrl}/system/restart/controller`);
        if (!response.ok) {
            throw new Error("Restart of controller failed.");
        }
    }

    /**
     * Restarts the operating system.
     */
    async restartOperatingSystem() {
        log.info("Restarting operating system");
        const response = await window.fetch(`${this.baseUrl}/system/restart/system`);
        if (!response.ok) {
            throw new Error("Restart of controller failed.");
        }
    }

    /**
     * Restarts the user interface.
     */
    async restartUserInterface() {
        log.info("Restarting user interface");
        const response = await window.fetch(`${this.baseUrl}/system/restart/lightdm`);
        if (!response.ok) {
            throw new Error("Restart of controller failed.");
        }
    }

    /**
     * Updates the software to currently defined version.
     */
    async updateSoftware() {
        log.info("Updating software");
        const response = await window.fetch(`${this.baseUrl}/system/software-update`);
        if (!response.ok) {
            throw new Error("Software update failed.");
        }
    }

    /**
     * Shuts the cube down.
     */
    async shutdown() {
        log.info("Shutting down");
        const response = await window.fetch(`${this.baseUrl}/system/shutdown`);
        if (!response.ok) {
            throw new Error("Shutdown failed.");
        }
    }

    /**
     * Logs the specified message.
     * @return Promise that resolves if the log entry was successfully created.
     */
    async log(level: LogLevel, source: string, message: string, data: any) {
        log.info("Logging message", {level, source, message, data});

        const logRequest = {level, source, message, data};
        const response = await window.fetch(`${this.baseUrl}/logs`, createJsonRequestInit("post", logRequest));
        if (!response.ok) {
            throw new Error("Could not log.");
        }
    }

    async getDevices() {
        const response = await window.fetch(`${this.baseUrl}/devices`);
        if (!response.ok) {
            throw new Error("Fetching devices failed.");
        }
        return await response.json() as Device[];
    }
}
